import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { logApiError, logUnexpectedError } from '../Utils/apiError';

function CheckEmailPage() {
  const [cooldown, setCooldown] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusTone, setStatusTone] = useState('info');

  useEffect(() => {
    if (window.fbq) {
      window.fbq('track', 'CompleteRegistration');
    }
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) clearInterval(timer);
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const getPendingEmail = () => {
    const pendingEmail = localStorage.getItem('pendingVerificationEmail');
    if (pendingEmail) return pendingEmail;

    const storedUser = localStorage.getItem('user');
    if (!storedUser) return '';

    try {
      const user = JSON.parse(storedUser);
      if (user && typeof user.email === 'string') return user.email;
    } catch {
      return '';
    }

    return '';
  };

  const handleResend = async () => {
    const emailString = getPendingEmail().trim();

    if (!emailString) {
      setStatusTone('error');
      setStatusMessage('We couldn\u2019t find the email address. Please go back and try logging in or registering again.');
      return;
    }

    setCooldown(30);
    setStatusMessage('');
    setStatusTone('info');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/public/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailString })
      });

      if (response.ok) {
        let data = {};
        try {
          data = await response.json();
        } catch {
          data = {};
        }

        if (data?.status === 'already_verified') {
          setStatusTone('success');
          setStatusMessage('Email already verified. Please log in.');
        } else {
          setStatusTone('success');
          setStatusMessage('Verification email sent. Please check your inbox and spam folder.');
        }
      } else {
        await logApiError(response, 'Resend verification failed');
        setStatusTone('error');
        setStatusMessage(
          response.status === 429
            ? 'Too many requests. Please wait a bit before trying again.'
            : 'Something went wrong. Please try again.'
        );
      }
    } catch (err) {
      logUnexpectedError(err, 'Resend verification request failed');
      setStatusTone('error');
      setStatusMessage('Something went wrong. Please try again.');
    }
  };

  const statusClasses = statusTone === 'success'
    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
    : statusTone === 'error'
      ? 'border-rose-200 bg-rose-50 text-rose-700'
      : 'border-blue-200 bg-blue-50 text-blue-700';

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto flex min-h-[calc(100vh-3rem)] w-full max-w-md items-center justify-center">
        <section className="w-full rounded-3xl border border-slate-200 bg-white p-6 text-center shadow-sm sm:p-8">
          <div className="mb-6">
            <div className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">
              Meshum
            </div>
            <h1 className="mt-4 text-2xl font-semibold tracking-tight text-slate-950 sm:text-3xl">
              Check your email
            </h1>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              We sent a verification link to your email address. Open that link to activate your Meshum account.
            </p>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              If it is not in your inbox, check your spam folder or resend the verification email.
            </p>
          </div>

          {statusMessage && (
            <div className={`rounded-xl border px-4 py-3 text-sm ${statusClasses}`}>
              {statusMessage}
            </div>
          )}

          <div className="mt-5 space-y-3">
            {cooldown > 0 ? (
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                Please wait {cooldown} second{cooldown !== 1 ? 's' : ''} before resending.
              </div>
            ) : (
              <button
                onClick={handleResend}
                className="h-12 w-full rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
              >
                Resend verification email
              </button>
            )}

            <Link
              to="/login"
              className="block h-12 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-center text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Back to login
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}

export default CheckEmailPage;
